import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import css from './FieldFile.module.css';
import { uploadFilesFirebase } from '../../util/api';
import IconCloud from '../IconCloud/IconCloud';
import { useIntl } from 'react-intl';

const MAX_FILE_SIZE_MB = 20;

const FieldFile = ({ id, name, form, values, folder, accept: acceptRaw }) => {
  const intl = useIntl();
  const fileInputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const currentFile = values?.[name] || null;

  const acceptMaybe = acceptRaw ? { accept: acceptRaw } : {};

  const handleFileUpload = (file) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return;
    }

    if (uploadFilesFirebase) {
      setIsUploading(true);
      uploadFilesFirebase(file, folder)
        .then(res => {
          if (res && res.success) {
            form.change(name, res.url);
          } else {
            console.error(`Document upload unsuccessful, response:`, res);
          }
        })
        .catch((error) => {
          console.error('Upload failed:', error);
        })
        .finally(() => {
          setIsUploading(false);
        });
    }
  };

  const preventDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    preventDefaults(e);
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    preventDefaults(e);
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    preventDefaults(e);
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
    e.target.value = null;
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div
      className={classNames(css.dropzone, {[css.activeDropzone]: isDragActive})}
      onDragEnter={handleDragEnter}
      onDragOver={preventDefaults}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={css.cloudIcon}>
        <IconCloud/>
      </div>

      {currentFile ?
        <a className={css.link} href={currentFile} download>
          <p className={css.dragText}>{intl.formatMessage({ id: 'FieldFile.uploadedFile'})}</p>
        </a>
      : <p className={css.dragText}>{intl.formatMessage({ id: 'FieldFile.uploadFile'})}</p>}

      <p className={css.maxSizeText}>
        {intl.formatMessage({ id: 'FieldFile.maxSizeLabel'}, { max: MAX_FILE_SIZE_MB })}
      </p>

      <input
        type="file"
        id={id}
        name={name}
        ref={fileInputRef}
        onChange={handleInputChange}
        className={css.hiddenFileInput}
        {...acceptMaybe}
      />

      <button
        type="button"
        className={css.browseButton}
        onClick={handleBrowseClick}
        disabled={isUploading}
      >
        {intl.formatMessage({ id: isUploading ? 'FieldFile.uploading' : 'FieldFile.cta'})}
      </button>
    </div>
  );
};

export default FieldFile;
